import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import langReducer from './features/lang/langSlice';
import trayReducer from './features/tray/traySlice';
import recentSearchesReducer from './features/savedSearchesSlice';
import comparisonPreferencesReducer, { setAllWeights } from './features/comparisonPreferencesSlice';
import jobFormReducer, { applyServerJobForm } from './features/jobFormSlice';
import uiReducer from './features/uiSlice';
import { sentryMiddleware } from './sentryMiddleware';
import type { UserStateMap } from '../types/user-state';
import { authPromptReducer } from './features/authPrompt/authPromptSlice';
import finlandFilterReducer from './features/finlandFilterSlice';
import { syncMatchProfileToCookies, type CookieMatchProfile } from '../utils/shared/client-preferences';
import { ensureLocalDataOwnerReconciled } from '../utils/persistence/local-data-owner';

// Shared state for localforage instance (module-level to persist across calls)
let localForagePromise: Promise<any> | null = null;
let localForageInstance: any = null;

// Lazy loader for localforage - dynamically imports only on client side
// Vite SSR config excludes 'localforage' from server bundling
const loadLocalForage = async () => {
  if (typeof window === 'undefined') {
    return null; // Server-side: no localforage
  }
  
  if (localForageInstance) {
    return localForageInstance; // Already loaded
  }
  
  if (!localForagePromise) {
    // Start loading localforage
    localForagePromise = import('localforage').then(m => {
      // CJS/UMD interop: in some bundling modes (e.g. Vite dev serving the raw UMD file)
      // the ESM namespace has no `default` and localforage lands on `window.localforage`.
      // Without this fallback every persistence op silently no-ops.
      const instance =
        (m as { default?: unknown }).default ??
        (typeof window !== 'undefined' ? (window as { localforage?: unknown }).localforage : null);
      if (!instance) {
        console.error('[createStore] localforage loaded without a usable export — persistence disabled');
        return null;
      }
      localForageInstance = instance;
      return localForageInstance;
    }).catch((err) => {
      console.error('[createStore] Failed to load localforage:', err);
      localForagePromise = null; // Reset on error so we can retry
      return null;
    });
  }
  
  return localForagePromise;
};

// Pre-load localforage on client side as soon as module loads
if (typeof window !== 'undefined') {
  loadLocalForage().catch(() => {
    // Silently fail - will use no-op storage
  });
}

// Getter function that returns localforage storage adapter
// This returns a storage-like object that works with redux-persist
const getLocalForage = () => {
  if (typeof window === 'undefined') {
    // Server-side: return a no-op storage object
    return {
      getItem: () => Promise.resolve(null),
      setItem: () => Promise.resolve(null),
      removeItem: () => Promise.resolve(null),
      clear: () => Promise.resolve(null),
    };
  }
  
  // Client-side: return a storage adapter that uses the loaded instance. Every op first waits
  // for the owner check (utils/persistence/local-data-owner.ts) so a previous account's
  // persisted jobForm/recentSearches are wiped before REHYDRATE can read them.
  const withInstance = <T,>(op: (instance: any) => Promise<T>) =>
    ensureLocalDataOwnerReconciled()
      .then(() => localForageInstance ?? loadLocalForage())
      .then((instance) => (instance ? op(instance) : null));
  return {
    getItem: (key: string) => withInstance((instance) => instance.getItem(key)),
    setItem: (key: string, value: any) => withInstance((instance) => instance.setItem(key, value)),
    removeItem: (key: string) => withInstance((instance) => instance.removeItem(key)),
    clear: () => withInstance((instance) => instance.clear()),
  };
};


const rootReducer = combineReducers({
  lang: langReducer,
  tray: trayReducer,
  recentSearches: recentSearchesReducer,
  comparisonPreferences: comparisonPreferencesReducer,
  jobForm: jobFormReducer,
  ui: uiReducer,
  authPrompt: authPromptReducer,
  finlandFilter: finlandFilterReducer,
});

/** Client-side singleton so SRP island and main app (e.g. saved-searches) share one store and persisted state. */
let clientStoreSingleton: ReturnType<typeof configureStore> | null = null;

/** Reset the client store singleton (for tests only). */
export function resetClientStoreSingletonForTests(): void {
  clientStoreSingleton = null;
}

/** Dispatches this call's SSR-provided userState's profile/preferences fields (for match-score
 *  calculation) into the still-Redux `jobForm`/`comparisonPreferences` draft slices — the
 *  viewed/saved/applied job state and the raw userState entries themselves are TanStack-only now
 *  (see `query/UserProvider.tsx`'s `initialUserState`), not Redux. */
function applySsrStateHydration(
  store: { dispatch: (action: unknown) => unknown },
  ssrUserState: UserStateMap,
  matchProfileFallback?: CookieMatchProfile | null,
): void {
  if (!ssrUserState || Object.keys(ssrUserState).length === 0) {
    // No signed-in profile/preferences to hydrate — fall back to the anonymous-visitor
    // `matchProfile` cookie (utils/shared/client-preferences.ts) so match score computes during
    // SSR for guests too, not just once the client mounts and redux-persist rehydrates from
    // IndexedDB. Only relevant to non-persisted, one-shot renders (job-detail's static shell) —
    // real persisted client stores (SrpApp, job.client.ts) already get this from IndexedDB.
    if (matchProfileFallback?.currentJob) {
      try {
        store.dispatch(setField({ field: 'currentJob', value: matchProfileFallback.currentJob }));
        store.dispatch(setField({ field: 'desiredJob', value: matchProfileFallback.desiredJob ?? {} }));
        if (matchProfileFallback.weights) {
          store.dispatch(setAllWeights(matchProfileFallback.weights));
        }
      } catch (error) {
        console.warn('[createStore] Failed to hydrate matchProfile cookie fallback:', error);
      }
    }
    return;
  }

  // Hydrate profile and preferences from userState for match score calculation
  const profileEntry = ssrUserState['profile'];
  const preferencesEntry = ssrUserState['preferences'];

  if (profileEntry?.data) {
    try {
      const profileData = profileEntry.data as any;
      // Same coercing path the client-side conflict resolver uses, so a stored profile that
      // predates newer fields (per-locale CV, workPercentage, the 2026-09 redesign fields) is
      // migrated instead of copied in raw.
      store.dispatch(applyServerJobForm(profileData));
    } catch (error) {
      console.warn('[createStore] Failed to hydrate profile from userState:', error);
    }
  }

  if (preferencesEntry?.data) {
    try {
      const preferencesData = preferencesEntry.data as any;
      store.dispatch(setAllWeights(preferencesData));
    } catch (error) {
      console.warn('[createStore] Failed to hydrate preferences from userState:', error);
    }
  }
}

export function createStore(pageContext: any) {
  const isServer = !pageContext.isClientSide;
  const ssrUserState: UserStateMap = pageContext.userState ?? {};
  const preloadedState = buildPreloadedState(pageContext);
  // Set by +data.ts (e.g. pages/job/@jobId/@seoSlug/+data.ts) when there's no signed-in
  // userState — see applySsrStateHydration's use of this.
  const matchProfileFallback: CookieMatchProfile | null =
    (pageContext as { data?: { matchProfileCookieFallback?: CookieMatchProfile | null } })?.data
      ?.matchProfileCookieFallback ?? null;

  // Reuse the same persisted store on the client so every caller (SRP island, job island, tools
  // pages, ...) shares one store and its persisted state — the job island used to get its own
  // throwaway store here, which meant recordViewedJob/saveJob dispatched from a job page never
  // reached localforage at all (My Jobs history always read back empty). Layer this call's own
  // SSR-provided userState (e.g. the current job's viewed/saved status) on top so it isn't lost.
  if (!isServer && typeof window !== 'undefined' && clientStoreSingleton) {
    // Same guarded cookie fallback as the first-creation path below: only used when this store's
    // rehydrated jobForm is still empty (no local profile), so a richer local profile is never
    // overwritten by the trimmed cookie.
    const existingJobForm = (clientStoreSingleton.getState() as { jobForm?: { currentJob?: { salary?: unknown; title?: unknown; normalisedTitle?: unknown } } }).jobForm;
    const hasLocalProfile = Boolean(
      existingJobForm?.currentJob &&
        (existingJobForm.currentJob.salary || existingJobForm.currentJob.title || existingJobForm.currentJob.normalisedTitle)
    );
    applySsrStateHydration(clientStoreSingleton, ssrUserState, hasLocalProfile ? null : matchProfileFallback);
    return clientStoreSingleton;
  }

  if (isServer) {
    // Server-side store without persistence
    const store = configureStore({
      reducer: rootReducer,
      preloadedState,
      devTools: false,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
          serializableCheck: false,
        }).concat(sentryMiddleware),
    });
    // matchProfileFallback only applies here: this is the true one-shot, non-persisted render
    // (real server-side, or job-detail's isClientSide:false static-shell match) — the persisted
    // client store branches below get their anonymous-visitor data from real redux-persist/
    // IndexedDB rehydration instead, which may be richer than the trimmed cookie and shouldn't be
    // overwritten by it.
    applySsrStateHydration(store, ssrUserState, matchProfileFallback);
    return store;
  }

  // Client-side, first store creation this session: persisted store.
  const persistConfig = {
    key: 'root',
    storage: getLocalForage(),
    whitelist: ['lang', 'tray', 'recentSearches', 'comparisonPreferences', 'jobForm', 'ui', 'finlandFilter'],
  };

  const persistedReducer = persistReducer(persistConfig, rootReducer);

  const store = configureStore({
    reducer: persistedReducer,
    preloadedState,
    // Enable DevTools in both environments for consistent debugging
    devTools: true,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(sentryMiddleware),
  });
  clientStoreSingleton = store;

  // Mirror jobForm/comparisonPreferences into a cookie whenever they change, so anonymous
  // visitors' match score can be computed during SSR too — see
  // utils/shared/client-preferences.ts's syncMatchProfileToCookies for why (redux-persist writes
  // to localforage/IndexedDB, which SSR can't read; this cookie is the anonymous-user counterpart
  // to userState.profile.data/.preferences.data, the signed-in path resolved in +data.ts).
  let lastSyncedMatchProfileJson = '';
  store.subscribe(() => {
    const state = store.getState() as unknown as {
      jobForm: Parameters<typeof syncMatchProfileToCookies>[0];
      comparisonPreferences: Parameters<typeof syncMatchProfileToCookies>[1];
    };
    const json = JSON.stringify([state.jobForm.currentJob, state.jobForm.desiredJob, state.comparisonPreferences]);
    if (json === lastSyncedMatchProfileJson) return;
    lastSyncedMatchProfileJson = json;
    syncMatchProfileToCookies(state.jobForm, state.comparisonPreferences);
  });

  // `jobForm`/`comparisonPreferences` are persisted, whitelisted slices, so redux-persist's async
  // REHYDRATE wholesale-replaces them once local storage has loaded. Applying this call's SSR
  // state before that would just get clobbered a moment later — so defer it to persistStore's
  // rehydrate-complete callback and let it layer on top instead.
  persistStore(store, undefined, () => {
    // If IndexedDB rehydration just populated a real local profile, it's richer than the trimmed
    // matchProfile cookie (see applySsrStateHydration's comment) — don't overwrite it. But when
    // IndexedDB has nothing (fresh session, cleared storage, first visit on this device), the
    // cookie is all we have, and skipping it here left match score permanently blank client-side
    // for anonymous visitors even though the static SSR shell — which does apply this fallback —
    // showed a real score. Same emptiness check calculateJobMatchScore itself uses.
    const rehydratedJobForm = (store.getState() as { jobForm?: { currentJob?: { salary?: unknown; title?: unknown; normalisedTitle?: unknown } } }).jobForm;
    const hasLocalProfile = Boolean(
      rehydratedJobForm?.currentJob &&
        (rehydratedJobForm.currentJob.salary || rehydratedJobForm.currentJob.title || rehydratedJobForm.currentJob.normalisedTitle)
    );
    applySsrStateHydration(store, ssrUserState, hasLocalProfile ? null : matchProfileFallback);
  });

  return store;
}

function buildPreloadedState(pageContext: any) {
  const baseState = pageContext.redux?.ssrState ? { ...pageContext.redux.ssrState } : {};

  // Ensure finlandFilter is enabled by default if not in baseState
  const finlandFilterState = baseState.finlandFilter || { enabled: true };

  return {
    ...baseState,
    finlandFilter: finlandFilterState,
  };
}

export type AppStore = ReturnType<typeof createStore>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];